// hooks/useDraftManagement.js
import { useState, useCallback } from 'react';
import { App } from 'antd';
import { extractSubdomain } from '../utils/domainUtils';
import { deserializeColor, isSerializedColor } from '../utils/colorUtils';
import { decryptCataloguePassword } from '../utils/cataloguePasswordUtils';

export const useDraftManagement = () => {
    const { message: messageApi } = App.useApp();
    const [draftFormData, setDraftFormData] = useState(null);
    const [currentDraftId, setCurrentDraftId] = useState(null);
    const [currentPublishedCatalogueId, setCurrentPublishedCatalogueId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const isDraftSavable = (() => {
        if (!draftFormData) return false;
        const hasName = !!draftFormData.catalogueName;
        const hasLogo = !!draftFormData.logo;
        const hasDomain = !!draftFormData.domain;
        const hasLicense = draftFormData.licenseList && draftFormData.licenseList.length > 0;
        const hasKeywords = draftFormData.seo && draftFormData.seo.keywords && draftFormData.seo.keywords.length > 0;
        return hasName || hasLogo || hasDomain || hasLicense || hasKeywords;
    })();

    /**
     * Recursively deserializes all color objects in the data structure
     * @param {*} data - The data to deserialize
     * @returns {*} The deserialized data
     */
    const deserializeData = useCallback((data) => {
        if (!data) return data;

        // If it's a serialized color object, convert it to a hex string
        if (isSerializedColor(data)) {
            return deserializeColor(data);
        }

        // If it's an array, deserialize each item
        if (Array.isArray(data)) {
            return data.map(item => deserializeData(item));
        }

        // If it's an object, deserialize each property
        if (typeof data === 'object') {
            const result = {};
            for (const key in data) {
                result[key] = deserializeData(data[key]);
            }
            return result;
        }

        // Otherwise, return the data as is
        return data;
    }, []);

    const mapDraftToFormData = useCallback((draft) => {

        // First, deserialize any color objects in the draft
        draft = deserializeData(draft);

        // Check if we have userModifications stored in the draft
        const userMods = draft.userModifications || {};

        const urlToFileObject = (url, fileName) => {
            if (!url) return null;
            return {
                uid: `-${Date.now()}`,
                name: fileName || 'image.png',
                status: 'done',
                url: url,
                thumbUrl: url,
                originFileObj: null,
            };
        };

        const getFileNameFromUrl = (url) => {
            if (!url) return 'image.png';
            try {
                const urlParts = new URL(url).pathname.split('/');
                return urlParts[urlParts.length - 1] || 'image.png';
            } catch (e) {
                return 'image.png';
            }
        };

        // Prioritize user modifications for logo URL
        const logoUrl = userMods.banner?.logo?.url ||
            userMods.logo?.url ||
            draft.urlLogo ||
            draft.catalogues_page_style?.catalogueLogo;

        const logoFile = logoUrl ?
            urlToFileObject(logoUrl, `logo-${getFileNameFromUrl(logoUrl)}`) : null;

        // Prioritize user modifications for banner images
        const bannerImage = userMods.banner?.bannerImage?.url ||
            userMods.bannerImage?.url ||
            draft.catalogues_page_style?.coverImg ?
            urlToFileObject(
                userMods.banner?.bannerImage?.url ||
                userMods.bannerImage?.url ||
                draft.catalogues_page_style.coverImg,
                `banner-${getFileNameFromUrl(userMods.banner?.bannerImage?.url || userMods.bannerImage?.url || draft.catalogues_page_style.coverImg)}`
            ) : null;

        const bannerImageMobile = userMods.banner?.bannerImageMobile?.url ||
            userMods.bannerImageMobile?.url ||
            draft.catalogues_page_style?.coverImgMobile ?
            urlToFileObject(
                userMods.banner?.bannerImageMobile?.url ||
                userMods.bannerImageMobile?.url ||
                draft.catalogues_page_style.coverImgMobile,
                `banner-mobile-${getFileNameFromUrl(userMods.banner?.bannerImageMobile?.url || userMods.bannerImageMobile?.url || draft.catalogues_page_style.coverImgMobile)}`
            ) : null;

        // Prioritize user modifications for featured image
        const featuredImage = userMods.seo?.featuredImage?.url ||
            userMods.featuredImage?.url ||
            draft.featuredImage ?
            urlToFileObject(
                userMods.seo?.featuredImage?.url ||
                userMods.featuredImage?.url ||
                draft.featuredImage,
                `featured-${getFileNameFromUrl(userMods.seo?.featuredImage?.url || userMods.featuredImage?.url || draft.featuredImage)}`
            ) : null;

        // Create the form data object, prioritizing user modifications
        const formData = {
            // Basic fields
            catalogueName: userMods.catalogueName || draft.nameCatalog || '',
            domain: userMods.domain || extractSubdomain(draft.webDomain) || '',

            // Permissions
            permissions: {
                public: userMods.permissions?.public !== undefined ? userMods.permissions.public : (draft.isPublic ?? true),
                requireLogin: userMods.permissions?.requireLogin !== undefined ? userMods.permissions.requireLogin : (draft.isLoginRequired ?? false),
                loginPassword: (() => {
                    if (userMods.permissions?.loginPassword !== undefined) {
                        return userMods.permissions.loginPassword;
                    }
                    // Decrypt the draft password to display it in the form
                    if (draft.loginPassword) {
                        try {
                            return decryptCataloguePassword(draft.loginPassword);
                        } catch (error) {
                            console.error('Error decrypting draft password:', error);
                            return '';
                        }
                    }
                    return '';
                })(),
                iframeOnlyAccess: userMods.permissions?.iframeOnlyAccess !== undefined ? userMods.permissions.iframeOnlyAccess : (draft.isIframeOnlyAccess ?? false),
                allowedDomain: userMods.permissions?.allowedDomain !== undefined ? userMods.permissions.allowedDomain : (draft.allowedDomain || ''),
            },

            // Customization with color deserialization
            customization: {
                primaryColor: deserializeColor(userMods.customization?.primaryColor || draft.catalogues_page_style?.primaryColor || '#46518a'),
                secondaryColor: deserializeColor(userMods.customization?.secondaryColor || draft.catalogues_page_style?.secondaryColor || '#929ACC'),
                backgroundColor: deserializeColor(userMods.customization?.backgroundColor || draft.catalogues_page_style?.backgroundColor || '#FFFFFF'),
                actionButtonColor: deserializeColor(userMods.customization?.actionButtonColor || draft.catalogues_page_style?.actionButtonColor || '#000000'),
                bannerBackgroundColor: deserializeColor(userMods.customization?.bannerBackgroundColor || draft.catalogues_page_style?.bannerBackgroundColor),
                cardCornerTopLeft: userMods.customization?.cardCornerTopLeft !== undefined ? userMods.customization.cardCornerTopLeft : (draft.catalogues_page_style?.cardCornerTopLeft || 0),
                cardCornerTopRight: userMods.customization?.cardCornerTopRight !== undefined ? userMods.customization.cardCornerTopRight : (draft.catalogues_page_style?.cardCornerTopRight || 0),
                cardCornerBottomRight: userMods.customization?.cardCornerBottomRight !== undefined ? userMods.customization.cardCornerBottomRight : (draft.catalogues_page_style?.cardCornerBottomRight || 0),
                cardCornerBottomLeft: userMods.customization?.cardCornerBottomLeft !== undefined ? userMods.customization.cardCornerBottomLeft : (draft.catalogues_page_style?.cardCornerBottomLeft || 0),
                buttonCornerTopLeft: userMods.customization?.buttonCornerTopLeft !== undefined ? userMods.customization.buttonCornerTopLeft : (draft.catalogues_page_style?.buttonCornerTopLeft || 0),
                buttonCornerTopRight: userMods.customization?.buttonCornerTopRight !== undefined ? userMods.customization.buttonCornerTopRight : (draft.catalogues_page_style?.buttonCornerTopRight || 0),
                buttonCornerBottomRight: userMods.customization?.buttonCornerBottomRight !== undefined ? userMods.customization.buttonCornerBottomRight : (draft.catalogues_page_style?.buttonCornerBottomRight || 0),
                buttonCornerBottomLeft: userMods.customization?.buttonCornerBottomLeft !== undefined ? userMods.customization.buttonCornerBottomLeft : (draft.catalogues_page_style?.buttonCornerBottomLeft || 0),
                bannerCornerTopLeft: userMods.customization?.bannerCornerTopLeft !== undefined ? userMods.customization.bannerCornerTopLeft : (draft.catalogues_page_style?.bannerCornerTopLeft || 0),
                bannerCornerTopRight: userMods.customization?.bannerCornerTopRight !== undefined ? userMods.customization.bannerCornerTopRight : (draft.catalogues_page_style?.bannerCornerTopRight || 0),
                bannerCornerBottomRight: userMods.customization?.bannerCornerBottomRight !== undefined ? userMods.customization.bannerCornerBottomRight : (draft.catalogues_page_style?.bannerCornerBottomRight || 0),
                bannerCornerBottomLeft: userMods.customization?.bannerCornerBottomLeft !== undefined ? userMods.customization.bannerCornerBottomLeft : (draft.catalogues_page_style?.bannerCornerBottomLeft || 0),
            },

            // Banner
            banner: {
                heightOption: userMods.banner?.heightOption || draft.catalogues_page_style?.heightOption || '100',
                height: userMods.banner?.height !== undefined ? userMods.banner.height : (draft.catalogues_page_style?.coverHeightDesktop || 100),
                heightMobileOption: userMods.banner?.heightMobileOption || draft.catalogues_page_style?.heightMobileOption || '100',
                heightMobile: userMods.banner?.heightMobile !== undefined ? userMods.banner.heightMobile : (draft.catalogues_page_style?.coverHeightMobile || 100),
                bannerImage: bannerImage,
                bannerImageMobile: bannerImageMobile,
                logoPosition: userMods.banner?.logoPosition || draft.catalogues_page_style?.catalogueLogoPosition || 'center',
                isLogoVisible: userMods.banner?.isLogoVisible !== undefined ? userMods.banner.isLogoVisible : (draft.catalogues_page_style?.isLogoVisible ?? true),
                isTitleVisible: userMods.banner?.isTitleVisible !== undefined ? userMods.banner.isTitleVisible : (draft.catalogues_page_style?.isTitleVisible ?? true),
                logo: logoFile,
            },

            // SEO - MAPPING CORRETTO
            seo: {
                title: userMods.seo?.title || draft.titleSeo || '',
                featuredImage: featuredImage,
                keywords: userMods.seo?.keywords || draft.list_keywords || [],
                // Mapping completo delle descrizioni multilingua
                metaDescriptions: {
                    it: userMods.seo?.metaDescriptions?.it || draft.descriptionIt || '',
                    en: userMods.seo?.metaDescriptions?.en || draft.descriptionEn || '',
                    fr: userMods.seo?.metaDescriptions?.fr || draft.descriptionFr || '',
                    es: userMods.seo?.metaDescriptions?.es || draft.descriptionEs || '',
                    de: userMods.seo?.metaDescriptions?.de || draft.descriptionDe || '',
                },
                // Campo legacy per retrocompatibilità
                metaDescription: userMods.seo?.metaDescription || draft.metaDescription || draft.descriptionIt || '',
            },

            licenseList: userMods.licenseList || [],
        };

        return formData;
    }, [deserializeData]);

    return {
        draftFormData,
        setDraftFormData,
        currentDraftId,
        setCurrentDraftId,
        currentPublishedCatalogueId,
        setCurrentPublishedCatalogueId,
        isEditMode,
        setIsEditMode,
        isDraftSavable,
        mapDraftToFormData,
        messageApi
    };
};